// Location detection and management service
import config from '../config/appConfig';
import { apicFetch } from './apiTokenService';

export class LocationService {
  constructor() {
    this.isSupported = 'geolocation' in navigator;
    this.lastKnownLocation = null;
    this.locationCache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get user's current location using browser geolocation API
   * @param {Object} options - Geolocation options
   * @returns {Promise<Object>} Location data
   */
  async getCurrentLocation(options = {}) {
    const defaultOptions = {
      enableHighAccuracy: false, // Use less accurate but faster method
      timeout: 5000, // Shorter timeout
      maximumAge: 300000 // 5 minutes
    };

    const geoOptions = { ...defaultOptions, ...options };

    if (!this.isSupported) {
      throw new Error('Geolocation is not supported by this browser');
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Location request timed out after 5 seconds'));
      }, geoOptions.timeout);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          clearTimeout(timeoutId);
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };

          try {
            // Try MCS reverse geocode first
            const addressData = await this.reverseGeocodeMCS(locationData.latitude, locationData.longitude);
            const fullLocationData = {
              ...locationData,
              ...addressData
            };

            this.lastKnownLocation = fullLocationData;
            resolve(fullLocationData);
          } catch (geocodeError) {
            console.warn('⚠️ MCS reverse geocode failed, trying fallback:', geocodeError);
            // Fallback to legacy reverse geocode
            try {
              const fallbackAddressData = await this.reverseGeocode(locationData.latitude, locationData.longitude);
              const fullLocationData = {
                ...locationData,
                ...fallbackAddressData
              };
              this.lastKnownLocation = fullLocationData;
              resolve(fullLocationData);
            } catch (fallbackError) {
              console.warn('⚠️ Fallback reverse geocode also failed:', fallbackError);
              // Return basic location data even if all reverse geocoding fails
              this.lastKnownLocation = locationData;
              resolve(locationData);
            }
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              errorMessage = 'Unknown location error';
          }

          reject(new Error(errorMessage));
        },
        geoOptions
      );
    });
  }

  /**
   * Reverse geocode coordinates using MCS API
   * @param {number} latitude 
   * @param {number} longitude 
   * @returns {Promise<Object>} Address data from MCS reverse geocode API
   */
  async reverseGeocodeMCS(latitude, longitude) {
    const cacheKey = `mcs_${latitude},${longitude}`;
    
    // Check cache first
    const cached = this.locationCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {
      console.log('🗺️ Using cached MCS reverse geocode result');
      return cached.data;
    }

    console.log('🗺️ Calling MCS reverse geocode API:', { latitude, longitude });

    try {
      // Uses APIC_BASE_URL_API_V2, not APIC_BASE_URL_API — this coordinates/reverse-geocode
      // endpoint is one of two (see appConfig.js) hosted under an extra /medicaid/qapath1
      // segment on the stress environment that the rest of the APIC endpoints don't have.
      const apiUrl = `${config.APIC_BASE_URL_API_V2}/hcb/v3/geolocation/geocodes/coordinates?latitude=${latitude}&longitude=${longitude}`;
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        throw new Error(`MCS Reverse Geocoding API responded with ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MCS reverse geocode response:', data);
      
      // Extract the first result from the results array
      if (data.results && data.results.length > 0) {
        const firstResult = data.results[0];
        
        // Parse the address string to extract city, state, and zip
        // Format: "930 N Switzer Canyon Dr, Flagstaff, AZ 86001"
        const addressParts = firstResult.address.split(',').map(part => part.trim());
        let city = '';
        let stateZip = '';
        let stateCode = '';
        let zipCode = '';
        
        if (addressParts.length >= 3) {
          city = addressParts[1]; // "Flagstaff"
          stateZip = addressParts[2]; // "AZ 86001"
          const stateZipParts = stateZip.split(' ');
          stateCode = stateZipParts[0]; // "AZ"
          zipCode = stateZipParts[1] || ''; // "86001"
        }
        
        const addressData = {
          address: firstResult.address,
          latitude: firstResult.latitude,
          longitude: firstResult.longitude,
          city: city,
          stateCode: stateCode,
          zipCode: zipCode,
          formattedAddress: `${city}, ${stateCode} ${zipCode}`.trim(),
          source: 'mcs-reverse-geocode'
        };

        // Cache the result
        this.locationCache.set(cacheKey, {
          data: addressData,
          timestamp: Date.now()
        });

        console.log('✅ Parsed address data:', addressData);
        return addressData;
      } else {
        throw new Error('No results returned from MCS reverse geocode API');
      }

    } catch (error) {
      console.error('❌ MCS reverse geocode error:', error);
      throw error;
    }
  }

  /**
   * Reverse geocode coordinates to readable address (legacy method using fallback API)
   * @param {number} latitude 
   * @param {number} longitude 
   * @returns {Promise<Object>} Address data
   */
  async reverseGeocode(latitude, longitude) {
    const cacheKey = `${latitude},${longitude}`;
    
    // Check cache first
    const cached = this.locationCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {

      return cached.data;
    }



    try {
      // Using a free geocoding API (you might want to replace with your preferred service)
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );

      if (!response.ok) {
        throw new Error(`Geocoding API responded with ${response.status}`);
      }

      const data = await response.json();
      
      const addressData = {
        city: data.city || data.locality || '',
        state: data.principalSubdivision || '',
        stateCode: data.principalSubdivisionCode || '',
        country: data.countryName || '',
        countryCode: data.countryCode || '',
        zipCode: data.postcode || '',
        formattedAddress: `${data.city || data.locality || ''}, ${data.principalSubdivisionCode || ''} ${data.postcode || ''}`.trim()
      };

      // Cache the result
      this.locationCache.set(cacheKey, {
        data: addressData,
        timestamp: Date.now()
      });


      return addressData;

    } catch (error) {

      throw error;
    }
  }

  /**
   * Geocode an address (zip, city/state, or full address) to coordinates using MCS API
   * @param {Object} addressParams - Address parameters (zip, city, state, address1)
   * @returns {Promise<Object>} Coordinates data
   */
  async geocodeMCS(addressParams = {}) {
    const { zip, city, state, address1 } = addressParams;
    
    console.log('🗺️ Calling MCS forwardGeocode API:', addressParams);

    try {
      // Build address string for forwardGeocode endpoint
      // Format: "address city state zip" or just "zip" if only zip is provided
      let addressString = '';
      
      if (address1 || city || state || zip) {
        const parts = [];
        if (address1) parts.push(address1);
        if (city) parts.push(city);
        if (state) parts.push(state);
        if (zip) parts.push(zip);
        addressString = parts.join(' ');
      }
      
      if (!addressString) {
        throw new Error('No address parameters provided for geocoding');
      }

      // Uses APIC_BASE_URL_API_V2, not APIC_BASE_URL_API — this forward-geocode endpoint
      // is the sibling of reverseGeocodeMCS's coordinates endpoint above (same
      // /hcb/v3/geolocation/geocodes/... family), which was already correctly switched to
      // APIC_BASE_URL_API_V2 during the stress-environment migration. This one was missed
      // at the time, leaving it on the old APIC_BASE_URL_API base — on the stress
      // environment that's very likely a dead/incorrect URL, causing manual "type an
      // address and submit" searches (as opposed to picking an autocomplete suggestion) to
      // fail and surface as "Unable to find that address..." for every user, not just PA.
      const apiUrl = `${config.APIC_BASE_URL_API_V2}/hcb/v3/geolocation/geocodes/addresses?address=${encodeURIComponent(addressString)}`;
      console.log('📍 Forward geocode URL:', apiUrl);
      
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        throw new Error(`MCS Forward Geocode API responded with ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MCS forwardGeocode response:', data);
      
      // API returns an array, take the first result
      const result = Array.isArray(data) ? data[0] : data;
      
      // Extract coordinates from response
      // Response format: { latitude, longitude, city, state, postalCode, county, formatted_address, country }
      if (result && result.latitude && result.longitude) {
        const coordsData = {
          latitude: parseFloat(result.latitude),
          longitude: parseFloat(result.longitude),
          city: result.city || city || '',
          stateCode: result.state || state || '',
          zipCode: result.postalCode || result.zip || zip || '',
          county: result.county || '',
          formattedAddress: result.formatted_address || `${result.city || ''}, ${result.state || ''} ${result.postalCode || ''}`.trim(),
          source: 'mcs-forward-geocode'
        };

        console.log('✅ Parsed coordinates data:', coordsData);
        return coordsData;
      } else {
        throw new Error('No coordinates returned from MCS forwardGeocode API');
      }

    } catch (error) {
      console.error('❌ MCS forwardGeocode error:', error);
      throw error;
    }
  }

  /**
   * Geocode using Google Places API (new Place class — replaces deprecated PlacesService)
   * @param {Object} addressParams - Address parameters (zip, city, state, address1)
   * @returns {Promise<Object>} Coordinates data
   */
  async geocodeGoogle(addressParams = {}) {
    const { zip, city, state, address1 } = addressParams;

    console.log('🗺️ Calling Google Places geocoding:', addressParams);

    try {
      // Build address string
      let addressString = '';

      if (address1 || city || state || zip) {
        const parts = [];
        if (address1) parts.push(address1);
        if (city) parts.push(city);
        if (state) parts.push(state);
        if (zip) parts.push(zip);
        addressString = parts.join(', ');
      }

      if (!addressString) {
        throw new Error('No address parameters provided for geocoding');
      }

      // Wait for Google Maps to load (up to 5 seconds)
      if (!window.google?.maps?.places) {
        console.log('⏳ Google Maps not loaded yet, waiting...');
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds (50 × 100ms)

        while (!window.google?.maps?.places && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        if (!window.google?.maps?.places) {
          console.error('❌ Google Maps failed to load after 5 seconds');
          throw new Error('Google Maps API not loaded after waiting');
        }

        console.log('✅ Google Maps loaded after waiting');
      }

      // Use the new Place.searchByText API.
      // This replaces the two-step PlacesService.textSearch → getDetails flow and
      // avoids the PlacesService deprecation warning for new API keys (post-Mar 2025).
      const { places } = await window.google.maps.places.Place.searchByText({
        textQuery: addressString,
        fields: ['location', 'addressComponents', 'formattedAddress'],
        language: 'en'
      });

      if (!places || places.length === 0) {
        throw new Error(`No results found for address: ${addressString}`);
      }

      console.log('✅ Google Places search results:', places);

      const place = places[0];

      // Extract coordinates — place.location is a LatLng, same interface as before
      const latitude = place.location?.lat();
      const longitude = place.location?.lng();

      // Parse address components
      // New API uses longText/shortText instead of long_name/short_name
      let extractedCity = '';
      let extractedStateCode = '';
      let extractedZipCode = '';

      if (place.addressComponents) {
        place.addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes('locality')) {
            extractedCity = component.longText;
          } else if (types.includes('administrative_area_level_1')) {
            extractedStateCode = component.shortText;
          } else if (types.includes('postal_code')) {
            extractedZipCode = component.shortText;
          }
        });
      }

      const coordsData = {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        city: extractedCity || city || '',
        stateCode: extractedStateCode || state || '',
        zipCode: extractedZipCode || zip || '',
        formattedAddress: place.formattedAddress || `${extractedCity}, ${extractedStateCode} ${extractedZipCode}`.trim(),
        source: 'google-places-geocode'
      };

      console.log('✅ Parsed Google coordinates data:', coordsData);
      return coordsData;

    } catch (error) {
      console.error('❌ Google geocoding error:', error);
      throw error;
    }
  }

  /**
   * Get location-based plan information from API
   * @param {Object} locationData - Location data with coordinates and/or address
   * @returns {Promise<Object>} Plan data
   */
  async getLocationBasedPlan(locationData) {

    const params = new URLSearchParams({
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      city: locationData.city,
      state: locationData.state,
      stateCode: locationData.stateCode,
      zipCode: locationData.zipCode
    }).toString();
    const apiUrl = `${config.API_BASE_URL}/mcs/api/location/plan?${params}`;

    try {
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Plan lookup API responded with ${response.status}: ${response.statusText}`);
      }

      const planData = await response.json();
      
      return {
        environmentId: planData.environmentId,
        state: planData.state || locationData.stateCode,
        planName: planData.planName || '',
        planCode: planData.planCode || '',
        linesOfBusiness: planData.linesOfBusiness || [],
        supportedServices: planData.supportedServices || []
      };

    } catch (error) {
      // Fallback plan determination based on state
      return this.getFallbackPlan(locationData);
    }
  }

  /**
   * Fallback plan determination when API is unavailable
   * @param {Object} locationData 
   * @returns {Object} Fallback plan data
   */
  getFallbackPlan(locationData) {


    // Default plan mapping based on state
    const statePlanMap = {
      'FL': {
        environmentId: '29',
        state: 'FL',
        planName: 'Florida Plan',
        planCode: 'FL001',
        linesOfBusiness: [
          { value: "long_term_care", label: "Long Term Care" },
          { value: "healthy_kids", label: "Healthy kids" },
          { value: "medicaid_mma", label: "Medicaid - MMA" }
        ]
      },
      'AZ': {
        environmentId: '6',
        state: 'AZ',
        planName: 'Arizona Plan',
        planCode: 'AZ001',
        linesOfBusiness: [
          { value: "medicaid", label: "Medicaid" },
          { value: "chip", label: "CHIP" }
        ]
      }
    };

    const stateCode = locationData.stateCode || locationData.state;
    const fallbackPlan = statePlanMap[stateCode] || null;


    return fallbackPlan;
  }

  /**
   * Get IP-based location as fallback
   * @returns {Promise<Object>} IP-based location data
   */
  async getIpBasedLocation() {


    try {
      const response = await fetch('https://ipapi.co/json/');
      
      if (!response.ok) {
        throw new Error(`IP location API responded with ${response.status}`);
      }

      const data = await response.json();
      
      const ipLocationData = {
        latitude: parseFloat(data.latitude) || null,
        longitude: parseFloat(data.longitude) || null,
        city: data.city || '',
        state: data.region || '',
        stateCode: data.region_code || '',
        country: data.country_name || '',
        countryCode: data.country_code || '',
        zipCode: data.postal || '',
        formattedAddress: `${data.city || ''}, ${data.region_code || ''} ${data.postal || ''}`.trim(),
        source: 'ip'
      };


      return ipLocationData;

    } catch (error) {

      throw error;
    }
  }

  /**
   * Full location detection flow with fallbacks
   * @returns {Promise<Object>} Complete location and plan data
   */
  async detectLocationAndPlan() {


    let locationData = null;
    let planData = null;
    let method = 'unknown';

    // Try GPS first (single attempt with short timeout)
    try {

      locationData = await this.getCurrentLocation();
      method = 'gps';

    } catch (gpsError) {

      
      // Try IP-based location (single attempt)
      try {
        locationData = await this.getIpBasedLocation();
        method = 'ip';

      } catch (ipError) {

        
        // All detection failed — propagate error rather than silently using fake coordinates
        throw ipError;
      }
    }

    // Get plan data for the location (skip API call, use direct mapping)
    try {

      planData = this.getFallbackPlan(locationData); // Use local mapping instead of API

    } catch (planError) {

      planData = this.getFallbackPlan({ stateCode: 'FL' });
    }

    const result = {
      location: locationData,
      plan: planData,
      detectionMethod: method,
      timestamp: Date.now()
    };


    return result;
  }

  /**
   * Clear cached location data
   */
  clearCache() {

    this.locationCache.clear();
    this.lastKnownLocation = null;
  }

  /**
   * Get last known location if available
   * @returns {Object|null} Last known location data
   */
  getLastKnownLocation() {
    return this.lastKnownLocation;
  }
}

// Create singleton instance
export const locationService = new LocationService();
export default locationService;
